import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { RefinedDataItem, RefinedDataReturn } from "@/lib/useQuery/useGetExportData";
import { cn } from "@/lib/utils";
import { createColumnHelper } from "@tanstack/react-table";
import { AlertCircle, Box, CheckCircle, ListChecks, MoreHorizontal, Settings, SlidersHorizontal, Text } from "lucide-react";
import { ValidationTableCell } from "./components/crm/_shared/tableCell";
import { countErrorsInRowForFields } from "./utils";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Link } from "react-router-dom";


const columnHelper = createColumnHelper<RefinedDataItem>();

type Props = {
    columnVisibility: Record<string, boolean>;
    data?: RefinedDataReturn
}

export const exportSurveyTableColumns = ({ columnVisibility, data }: Props) => {
    const visibleColumns = Object.keys(columnVisibility).filter((key) => columnVisibility[key] === true)
    return [
        columnHelper.display({
            id: 'status',
            header: () => (
                <div className='grid place-items-center'>
                    <TooltipProvider>
                        <div className="flex justify-center w-full" >
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <ListChecks className='text-sm' size={20} />
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>Validation Status</p>
                                </TooltipContent>
                            </Tooltip>
                        </div>
                    </TooltipProvider>
                </div>
            ),
            cell: (params) => (
                <div className={cn(`grid place-items-center rounded-md text-white h-full`, {
                    'bg-green-500': countErrorsInRowForFields(params.row.original, visibleColumns) === 0,
                    'bg-red-500': countErrorsInRowForFields(params.row.original, visibleColumns) > 0
                })}>
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                {
                                    countErrorsInRowForFields(params.row.original, visibleColumns) === 0
                                        ? <CheckCircle />
                                        : <AlertCircle />
                                }
                            </TooltipTrigger>
                            <TooltipContent>
                                {countErrorsInRowForFields(params.row.original, visibleColumns) === 0 ? <p>Valid</p> : (
                                    <div className="">
                                        <p>Invalid, row lacks following fields:</p>
                                        <div className="text-xs">
                                            {/* TODO Dynamically check for errors */}
                                        </div>
                                    </div>
                                )}
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
            ),
            size: Number.MIN_SAFE_INTEGER,
            maxSize: 25,
            minSize: 25,
        }),
        columnHelper.display({
            id: 'question',
            header: () => (
                <div className='flex items-center gap-2'>
                    <Text size={20}/>
                    <span className='table-header__text'>Question</span>
                </div>
            ),
            cell: (params) => (
                <div className="p-2 truncate">
                    {params.row.original['Question'].value}
                </div>
            ),
        }),

        ...(Object.keys(columnVisibility) ?? []).map((key) => columnHelper.display({
            id: key,
            header: () => (
                <div className='flex items-center gap-2'>
                    <Settings size={20} />
                    <span className='table-header__text'>
                        {key}
                    </span>
                </div>
            ),
            cell: (params) => (
                // <div className="">{JSON.stringify(params.row.original[key])}</div>
                <ValidationTableCell field={params.row.original[key]} columnName={key} />
            ),
            maxSize: Number.MAX_SAFE_INTEGER,
        })),

        columnHelper.display({
            id: 'values',
            header: () => (
                <div className='flex items-center gap-2'>
                    <Box size={20} />
                    <span className='table-header__text'>Values</span>
                </div>
            ),
            cell: (params) => (
                <ValidationTableCell field={params.row.original['Values']} columnName="Values" />
            ),
            maxSize: Number.MAX_SAFE_INTEGER,
        }),
        columnHelper.display({
            id: 'actions',
            header: () => (
                <div className='flex items-center'>
                    <SlidersHorizontal size={20} />
                </div>
            ),
            cell: (params) => (
                <div className="grid h-full place-items-center">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild className="cursor-pointer">
                            <MoreHorizontal size={20} />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuSeparator/>
                            <DropdownMenuItem asChild >
                                <Link 
                                to={`/organization/${data?.organization?.id}/project/${data?.project.id}/survey/${data?.survey.id}/manage-survey?questionId=${params.row.original.questionId.value}`}
                                >
                                    Edit Question
                                </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild >
                                <Link 
                                to={`/organization/${data?.organization?.id}/project/${data?.project.id}/survey/${data?.survey.id}/manage-survey/questions?questionId=${params.row.original.questionId.value}`}
                                >
                                    Edit Valid Value
                                </Link>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            ),
            minSize: 25,
            maxSize: 25,
        }),
    ]
}

